import { Phone, Mail, MapPin, MessageCircle, Shield, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { trackPhoneClick, trackWhatsAppClick, trackEmailClick } from '../lib/analytics';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://panthera-umzug.de';

export default function KontaktPage() {
  const { t } = useLanguage();
  const k = t.kontakt;

  return (
    <>
      <SEOHead title="Kontakt | Panthera Umzug Berlin" description="Kontaktieren Sie Panthera Umzug für ein kostenloses Angebot." canonical={`${BASE_URL}/kontakt`} />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-black overflow-hidden" data-testid="kontakt-hero">
        <div className="absolute inset-0"><div className="absolute top-20 right-1/3 w-80 h-80 bg-[#D4AF37] rounded-full filter blur-[140px] opacity-15" /></div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight" data-testid="kontakt-h1">
            <span className="text-white">{k.h1_1}</span>
            <span className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] bg-clip-text text-transparent">{k.h1_2}</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">{k.subtitle}</p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-[#0a0a0a]" data-testid="kontakt-cards">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <a href="tel:+491631945793" onClick={() => trackPhoneClick()} className="block group" data-testid="kontakt-phone-card">
              <Card className="bg-[#111] border-[#2a2a2a] hover:border-[#D4AF37]/50 transition-all duration-300 h-full text-center group-hover:scale-[1.03]">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#8F6B1F]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <p className="text-white font-bold text-lg mb-1">{k.phone}</p>
                  <p className="text-[#D4AF37] font-medium">+49 163 1945793</p>
                </CardContent>
              </Card>
            </a>

            <a href="https://wa.me/491631945793" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick()} className="block group" data-testid="kontakt-whatsapp-card">
              <Card className="bg-[#111] border-[#2a2a2a] hover:border-[#25D366]/50 transition-all duration-300 h-full text-center group-hover:scale-[1.03]">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#25D366]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-7 h-7 text-[#25D366]" />
                  </div>
                  <p className="text-white font-bold text-lg mb-1">{k.whatsapp}</p>
                  <p className="text-[#25D366] font-medium">{k.whatsappAction}</p>
                </CardContent>
              </Card>
            </a>

            <a href="mailto:info@panthera-umzug.de" onClick={() => trackEmailClick()} className="block group" data-testid="kontakt-email-card">
              <Card className="bg-[#111] border-[#2a2a2a] hover:border-[#D4AF37]/50 transition-all duration-300 h-full text-center group-hover:scale-[1.03]">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#8F6B1F]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <p className="text-white font-bold text-lg mb-1">{k.email}</p>
                  <p className="text-[#D4AF37] font-medium">info@panthera-umzug.de</p>
                </CardContent>
              </Card>
            </a>

            <div data-testid="kontakt-location-card">
              <Card className="bg-[#111] border-[#2a2a2a] h-full text-center">
                <CardContent className="pt-8 pb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#8F6B1F]/20 flex items-center justify-center">
                    <MapPin className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <p className="text-white font-bold text-lg mb-1">{k.location}</p>
                  <p className="text-gray-400 font-medium">{k.locationVal}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Trust + CTA */}
      <section className="py-20 bg-black" data-testid="kontakt-trust">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Trust badges */}
            <div className="grid sm:grid-cols-3 gap-6 mb-16">
              <div className="flex items-start gap-4 p-5 bg-[#111] rounded-xl border border-[#2a2a2a]">
                <Clock className="w-6 h-6 text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <p className="text-white font-bold text-sm">{k.sidebarFast}</p>
                  <p className="text-gray-400 text-sm mt-1">{k.sidebarFastDesc}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-[#111] rounded-xl border border-[#2a2a2a]">
                <Shield className="w-6 h-6 text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <p className="text-white font-bold text-sm">{k.sidebarFree}</p>
                  <p className="text-gray-400 text-sm mt-1">{k.sidebarFreeDesc}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-[#111] rounded-xl border border-[#2a2a2a]">
                <CheckCircle className="w-6 h-6 text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <p className="text-white font-bold text-sm">{k.sidebarInsured}</p>
                  <p className="text-gray-400 text-sm mt-1">{k.sidebarInsuredDesc}</p>
                </div>
              </div>
            </div>

            {/* Big CTA */}
            <div className="text-center p-10 bg-gradient-to-br from-[#D4AF37]/10 to-[#8F6B1F]/10 border border-[#D4AF37]/30 rounded-2xl">
              <p className="text-2xl font-bold text-white mb-2">{k.quickHelp}</p>
              <p className="text-gray-300 mb-8">{k.quickHelpDesc}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="tel:+491631945793" onClick={() => trackPhoneClick()} className="w-full sm:w-auto">
                  <Button size="lg" className="w-full bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] text-black font-bold px-8 py-6 text-base rounded-lg hover:scale-105 transition-transform shadow-lg shadow-[#D4AF37]/30" data-testid="kontakt-cta-call">
                    <Phone className="mr-2 w-5 h-5" /> +49 163 1945793
                  </Button>
                </a>
                <a href="https://wa.me/491631945793" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick()} className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-bold px-8 py-6 text-base rounded-lg hover:scale-105 transition-all" data-testid="kontakt-cta-whatsapp">
                    <MessageCircle className="mr-2 w-5 h-5" /> WhatsApp
                  </Button>
                </a>
                <a href="mailto:info@panthera-umzug.de" onClick={() => trackEmailClick()} className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-bold px-8 py-6 text-base rounded-lg hover:scale-105 transition-all" data-testid="kontakt-cta-email">
                    <Mail className="mr-2 w-5 h-5" /> E-Mail
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
